import { FormikProps, useFormikContext } from 'formik'
import { ScheduleType } from '../../../../modules/privateOffer/types'

import { debounce } from 'lodash'
import { useRef, useCallback, useMemo } from 'react'
import { BoxV4 } from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { getAmiInstallmentsBasedOnSchedule } from '../../../../utils/amiPrivateOfferUtil'
import { InstallmentsSection } from '../../../PrivateOfferComponents/InstallmentsSection/InstallmentsSection'
import { TotalContractValueSection } from '../../../../common/components/TotalContractValueSection/TotalContractValueSection'
import { useStyles } from './ResaleInstallmentsSection.styles'
import { ResaleAuthorizationWithErrors } from '../../../../modules/resaleAuthorization/types'
import {
  addMonthsToDate,
  addOneDayToDate,
  calculateDurationInMonths,
} from '../../helpers'

export { calculateDurationInMonths } from '../../helpers'

export const ResaleInstallmentsSection = () => {
  const { values, touched, errors, setFieldValue, handleBlur } =
    useFormikContext() as FormikProps<ResaleAuthorizationWithErrors>
  const formikProps =
    useFormikContext() as FormikProps<ResaleAuthorizationWithErrors>
  const { classes } = useStyles()
  const minDate = useMemo(() => {
    if (values.agreementExpirationDate) {
      return addOneDayToDate(values.agreementExpirationDate as string)
    }

    return new Date().toISOString().split('T')[0]
  }, [values.agreementExpirationDate])
  const monthsToAdd = values?.contractDurationInMonthsForEndCustomer
    ? values.contractDurationTimeUnit === 'YEARS'
      ? values?.contractDurationInMonthsForEndCustomer * 12
      : values?.contractDurationInMonthsForEndCustomer
    : 1

  const baseDate = new Date().toISOString().split('T')[0]
  const maxDate = baseDate ? addMonthsToDate(baseDate, monthsToAdd) : undefined

  // Debounced function to regenerate installments
  const debouncedRegenerateInstallments = useRef(
    debounce(
      (
        tcv: number,
        containerOfferServiceLength: string | number | undefined,
        flexiblePaymentType: ScheduleType | undefined,
        offerStartDate: string | undefined,
        setFieldValueFn: (field: string, value: any) => Promise<any>,
        timeUnit: string,
        minimumDate: string
      ) => {
        const durationInMonths = calculateDurationInMonths(
          containerOfferServiceLength,
          timeUnit
        )
        const paymentSchedule = flexiblePaymentType || ScheduleType.MONTHLY
        const effectiveOfferStartDate = minimumDate as string

        // Only regenerate if not CUSTOM
        if (paymentSchedule !== ScheduleType.CUSTOM && durationInMonths) {
          const newInstallments = getAmiInstallmentsBasedOnSchedule({
            tcv: tcv,
            durationInMonths: durationInMonths,
            paymentSchedule: paymentSchedule as ScheduleType,
            now: effectiveOfferStartDate,
          })
          setFieldValueFn('installmentInfo', newInstallments)
        }
      },
      500
    )
  ).current

  const handleTcvChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = (event.target as HTMLInputElement).value
      const numericValue = value !== '' ? Number(value) : undefined
      await setFieldValue('tcv', numericValue)

      // Regenerate installments if containerOfferServiceLength is already set (debounced)
      const serviceLength = values.contractDurationInMonthsForEndCustomer || ''
      if (numericValue && serviceLength) {
        debouncedRegenerateInstallments(
          numericValue,
          serviceLength,
          values.flexiblePaymentType,
          values.offerStartDate,
          setFieldValue,
          values.contractDurationTimeUnit as string,
          minDate as string
        )
      }
    },
    [
      setFieldValue,
      values.contractDurationInMonthsForEndCustomer,
      values.flexiblePaymentType,
      values.offerStartDate,
      debouncedRegenerateInstallments,
      values.contractDurationTimeUnit,
      minDate,
    ]
  )

  const durationInMonths = useMemo(
    () =>
      calculateDurationInMonths(
        values.contractDurationInMonthsForEndCustomer || '',
        values.contractDurationTimeUnit as string
      ),
    [
      values.contractDurationInMonthsForEndCustomer,
      values.contractDurationTimeUnit,
    ]
  )

  return (
    <div data-testid="resale-installments-section">
      <BoxV4
        heading={'Installments'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '18px', height: '14px' }}
          />
        }
      >
        <div className={classes.sectionContainer}>
          {values?.flexiblePaymentType !== ScheduleType.CUSTOM && (
            <TotalContractValueSection
              tcvValue={values?.tcv}
              firstInstallmentDateValue={values?.firstInstallmentDate}
              touched={{
                tcv: touched.tcv,
                firstInstallmentDate: touched.firstInstallmentDate,
              }}
              errors={{
                tcv: errors.tcv as string | undefined,
                firstInstallmentDate: errors.firstInstallmentDate as
                  | string
                  | undefined,
              }}
              onTcvChange={handleTcvChange}
              onFirstInstallmentDateChange={async date => {
                await setFieldValue('firstInstallmentDate', date)

                // Regenerate all installments with the new first installment date
                const serviceLength =
                  values.contractDurationInMonthsForEndCustomer || ''
                const tcv = values.tcv
                const paymentSchedule =
                  values.flexiblePaymentType || ScheduleType.MONTHLY

                if (
                  paymentSchedule !== ScheduleType.CUSTOM &&
                  tcv &&
                  serviceLength &&
                  date
                ) {
                  const durationInMonths = calculateDurationInMonths(
                    serviceLength,
                    values.contractDurationTimeUnit as string
                  )
                  const newInstallments = getAmiInstallmentsBasedOnSchedule({
                    tcv: tcv,
                    durationInMonths: durationInMonths,
                    paymentSchedule: paymentSchedule as ScheduleType,
                    now: date,
                  })
                  await setFieldValue('installmentInfo', newInstallments)
                } else if (
                  values?.installmentInfo &&
                  values?.installmentInfo?.length > 0
                ) {
                  // Fallback: only update first installment if we can't regenerate
                  await setFieldValue('installmentInfo[0].paymentDate', date)
                }
              }}
              formik={
                formikProps as unknown as FormikProps<Record<string, unknown>>
              }
              onBlur={handleBlur}
              minDate={minDate as string}
              maxDate={maxDate as string}
            />
          )}
          <InstallmentsSection
            installments={values?.installmentInfo}
            tcv={values?.tcv}
            durationInMonths={durationInMonths}
            firstInstallmentDate={values?.firstInstallmentDate}
            handleChangeInstallmentsAmount={async updatedInstallments => {
              await setFieldValue('installmentInfo', updatedInstallments)
            }}
            key={'installmentInfo'}
            isResale={true}
            minDate={minDate as string}
            maxDate={maxDate as string}
          />
        </div>
      </BoxV4>
    </div>
  )
}
